import React, {useState, useMemo, useEffect, useRef} from "react";
import {Box, Divider, Paper, Stack, Button, Typography, CircularProgress, Alert} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DownloadIcon from "@mui/icons-material/Download";
import {Document, Page, pdfjs} from "react-pdf/dist/umd/entry.webpack";

import { SubtitleInline } from "components/shared/Typography";
import FileUploadDragAndDrop from "components/shared/FileUploadDragAndDrop";
import PDFFieldHighlighter from "./PDFFieldHighlighter";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function CertificateFileDisplay({
  certificateFiles,
  supplementalCoiFiles={},
  fileSelected,
  handleFileUpload,
  enableDownload,
  onRemove,
  hasEditPrivileges,
  disableUpload = false,
  highlightedField = null,
  fieldConfidence = null,
}) {

  const [pageSelected, setPageSelected] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    setPageSelected(1);
    setLoadError(null);
  }, [fileSelected])
  
  const emptyCertFile = !certificateFiles?.length && !Object.keys(supplementalCoiFiles).length
  const fileDisplayed = useMemo(() => {
    if (fileSelected?.isSupplementalCoiFile && fileSelected.insuranceType && fileSelected.documentType) {
      return supplementalCoiFiles[fileSelected.insuranceType][fileSelected.documentType];
    } else {

      return certificateFiles?.find((file) => {
        return file.name === fileSelected?.name
      })
    }
  }, [fileSelected, certificateFiles?.length, supplementalCoiFiles])

  const onDocumentLoadSuccess = ({numPages}) => {
    setNumPages(numPages);
    setIsLoading(false);
    setLoadError(null);

    // Get PDF dimensions after loading
    setTimeout(() => {
      if (pdfContainerRef.current) {
        const pdfCanvas = pdfContainerRef.current.querySelector('.react-pdf__Page__canvas');
        if (pdfCanvas) {
          setPdfDimensions({
            width: pdfCanvas.width,
            height: pdfCanvas.height
          });
        }
      }
    }, 100);
  }
  const onLoadError = (error) => {
    console.error('Error while loading document!', error);
    console.error('Error message:', error.message);
    console.error('File URL:', fileDisplayed?.content);
    setLoadError(error.message || 'Failed to load PDF file');
    setIsLoading(false);
  }
  
  const onDocumentLoadStart = () => {
    setIsLoading(true);
    setLoadError(null);
  }

  return (
    <Paper elevation={1} sx={{height: "100% !important", pb: 2, backgroundColor: "lightgrey"}}>
      {
        !!emptyCertFile ?
          <Stack sx={{minWidth: "800px", minHeight: "1035px", pt: 4,}}>
            {
              disableUpload &&
              <SubtitleInline
                sx={{
                  alignSelf: "center",
                  color: "gray"
                }}
              >{"NO FILE SELECTED FOR PREVIEW"}</SubtitleInline>
            }
            {
              !disableUpload &&
              <FileUploadDragAndDrop
                buttonText="SELECT OR DRAG AND DROP YOUR ACORD 25 PDF"
                buttonProps={{
                  startIcon: <UploadIcon />,
                  component:"span",
                  size:"large",
                }}
                sx={{
                  padding: 10,
                }}
                onChange={event => handleFileUpload(event, true)}
              />
            }
          </Stack>
          :
          <Stack ml={2} mr={2}>
            <Stack direction="row" alignItems={"center"}>
              <SubtitleInline>Pages</SubtitleInline>
              {
                <>
                  <Button
                    variant="text"
                    disabled={pageSelected==1}
                    sx={{visibility: numPages > 1 ? "visible" : "hidden"}}
                    onClick={()=>setPageSelected(selected => selected - 1)}
                  >
                    <KeyboardArrowLeftIcon />
                  </Button>
                  <SubtitleInline>{pageSelected}/{numPages}</SubtitleInline>
                  <Button
                    variant="text"
                    disabled={pageSelected==numPages}
                    sx={{visibility: numPages > 1 ? "visible" : "hidden"}}
                    onClick={()=>setPageSelected(selected => selected + 1)}
                  >
                    <KeyboardArrowRightIcon />
                  </Button>
                </>
              }
              <SubtitleInline sx={{margin:"auto"}}>{fileSelected?.name}</SubtitleInline>
              {
                !!enableDownload &&
                <Button
                  disabled={!hasEditPrivileges}
                  startIcon={<DownloadIcon />}
                  href={fileDisplayed?.content}
                  download={fileSelected?.name || "certificate.pdf"}
                  sx={{visibility: fileDisplayed?.isNew ? "hidden" : "visible"}}
                >
                  Download
                </Button>
              }
              {
                !!onRemove &&
                <Button
                  onClick={onRemove}
                >
                  Remove
                </Button>
              }
            </Stack>
            <Divider />
            {
              (certificateFiles?.length || Object.keys(supplementalCoiFiles).length) && fileSelected &&
              <Box ref={pdfContainerRef} sx={{width: "800px", minHeight: "1035px", alignSelf: "center", position: "relative"}}>
                {isLoading && (
                  <Box sx={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 5}}>
                    <CircularProgress />
                  </Box>
                )}
                {loadError && (
                  <Alert severity="error" sx={{margin: 2, zIndex: 5}}>
                    {loadError}
                    <br />
                    <Typography variant="caption">
                      URL: {fileDisplayed?.content?.substring(0, 100)}...
                    </Typography>
                  </Alert>
                )}
                <Document
                  className="certificate-file"
                  file={fileDisplayed?.content}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onLoadError}
                  onLoadStart={onDocumentLoadStart}
                  loading={<CircularProgress />}
                >
                  <Page pageNumber={pageSelected} />
                </Document>
                {/* PDF Field Highlighter Overlay */}
                <PDFFieldHighlighter
                  fieldConfidence={fieldConfidence}
                  highlightedField={highlightedField}
                  pdfWidth={pdfDimensions.width}
                  pdfHeight={pdfDimensions.height}
                  pageNumber={pageSelected}
                />
              </Box>
            }
          </Stack>
      }
    </Paper>
  );
}
